import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      fetchInvitation();
    } else {
      setLoading(false);
      toast.error('Invalid invitation link');
    }
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const response = await axios.get(`http://localhost:3001/api/teams/invitation/${token}`);
      setInvitation(response.data);
    } catch (error) {
      toast.error('Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setProcessing(true);
    try {
      const authToken = localStorage.getItem('token');
      await axios.post(
        `http://localhost:3001/api/teams/invitation/${token}/accept`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` }}
      );
      
      toast.success('Invitation accepted! Welcome to the team!');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    setProcessing(true);
    try {
      await axios.post(`http://localhost:3001/api/teams/invitation/${token}/decline`);
      toast.success('Invitation declined');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error('Failed to decline invitation');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading invitation...</p>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">
            This invitation link is invalid or has expired.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Team Invitation</h2>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <p className="text-gray-600 mb-4">You've been invited to join:</p>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="font-semibold">Team:</span>
              <span>{invitation.team_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold">Role:</span>
              <span className="capitalize">{invitation.role}</span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold">Invited by:</span>
              <span>{invitation.invited_by_name}</span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleAccept}
            disabled={processing}
            className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:bg-green-300 transition-colors font-semibold"
          >
            {processing ? 'Processing...' : 'Accept Invitation'}
          </button>
          
          <button
            onClick={handleDecline}
            disabled={processing}
            className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 transition-colors"
          >
            Decline
          </button>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6">
          By accepting, you agree to join this team and collaborate with other members.
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitation;